import {
  AlertOctagon,
  AlertTriangle,
  ChevronDown,
  ClipboardList,
  HelpCircle,
  ShieldAlert,
  type LucideIcon
} from "lucide-react"
import { useState } from "react"

import { usePmbSection } from "~/hooks/pre-meeting"
import { type PmbSectionCtx } from "~/api/pre-meeting-v2"
import { type CoachingPackPayload } from "~/types/pre-meeting"

import { SectionShell } from "../section-ui"

// Coaching pack accent (matches SECTION_STYLES.coaching_pack).
const ACCENT = "#f59e0b"
const ACCENT_SOFT = "rgba(245,158,11,0.10)"

type BlockKey = "objections" | "questions" | "red_flags" | "industry_landmines"

const BLOCKS: Array<{
  key: BlockKey
  title: string
  subtitle: string
  icon: LucideIcon
}> = [
  {
    key: "objections",
    title: "Likely objections",
    subtitle: "Anticipate the pushback and pre-load your response",
    icon: ShieldAlert
  },
  {
    key: "questions",
    title: "Questions to ask",
    subtitle: "Open-ended prompts that advance the deal",
    icon: HelpCircle
  },
  {
    key: "red_flags",
    title: "Red flags to avoid",
    subtitle: "Common ways reps stumble with this persona",
    icon: AlertTriangle
  },
  {
    key: "industry_landmines",
    title: "Industry landmines",
    subtitle: "Compliance and cultural pitfalls for their industry",
    icon: AlertOctagon
  }
]

function CoachBlock({
  block,
  items,
  emphasized
}: {
  block: (typeof BLOCKS)[number]
  items: string[]
  emphasized: boolean
}) {
  const [open, setOpen] = useState(true)
  const isOpen = emphasized || open
  const Icon = block.icon

  return (
    <div
      className="rounded-xl border border-light-border dark:border-border bg-white dark:bg-dark-2 overflow-hidden transition-all"
      style={
        emphasized
          ? { borderColor: ACCENT, boxShadow: `0 0 0 1px ${ACCENT_SOFT}` }
          : undefined
      }
    >
      <button
        type="button"
        onClick={() => !emphasized && setOpen((v) => !v)}
        className={`w-full flex items-center gap-2.5 px-4 py-3 text-left ${emphasized ? "cursor-default" : "cursor-pointer"}`}
      >
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: ACCENT_SOFT, color: ACCENT }}
        >
          <Icon size={14} />
        </span>
        <span className="flex-1">
          <span className="block text-[13px] font-semibold text-dark-2 dark:text-dark-6">
            {block.title}
            {emphasized && (
              <span
                className="ml-2 text-[9px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full align-middle"
                style={{ background: ACCENT_SOFT, color: ACCENT }}
              >
                Focus
              </span>
            )}
          </span>
          <span className="block text-[11px] text-gray-500 dark:text-text-gray-300 mt-0.5">
            {block.subtitle}
          </span>
        </span>
        {!emphasized && (
          <ChevronDown
            size={16}
            className={`flex-shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-3 pt-0">
          <div className="border-t border-light-border/60 dark:border-border/60 pt-1">
            {items.map((it, i) => (
              <div key={i} className="flex items-start gap-2.5 py-2">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[7px]"
                  style={{ background: ACCENT }}
                />
                <p className="flex-1 text-[12.5px] leading-[1.6] text-dark-2 dark:text-dark-6">
                  {it}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Presentational view: shared by the drawer and the ASKOS agent widget.
export function CoachingPackSectionView({
  payload,
  isLoading = false,
  error = null
}: {
  payload?: CoachingPackPayload | null
  isLoading?: boolean
  error?: string | null
}) {
  const p = payload
  const emphasis = p?.emphasis === "questions" ? "questions" : "objections"
  const blocks = BLOCKS.map((block) => ({
    block,
    items: (p?.[block.key] as string[] | undefined) || []
  })).filter((b) => b.items.length > 0)
  const isEmpty = blocks.length === 0

  return (
    <SectionShell
      id="coaching_pack"
      styleKey="coaching_pack"
      icon={<ClipboardList size={16} />}
      title="Coaching Pack"
      subtitle="Prep for the conversational dynamics of this meeting"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyText="No coaching pack generated for this person yet."
    >
      <div className="flex flex-col gap-3">
        {blocks.map(({ block, items }) => (
          <CoachBlock
            key={block.key}
            block={block}
            items={items}
            emphasized={block.key === emphasis}
          />
        ))}
      </div>
    </SectionShell>
  )
}

export function CoachingPackSection({
  ctx,
  enabled
}: {
  ctx: PmbSectionCtx
  enabled: boolean
}) {
  const { data, isLoading, isError } = usePmbSection<CoachingPackPayload>(
    "coaching_pack",
    ctx,
    enabled
  )
  const error =
    data?.error || (isError ? "Could not load the coaching pack." : null)
  return (
    <CoachingPackSectionView
      payload={data?.payload}
      isLoading={isLoading}
      error={error}
    />
  )
}
